<template>
  <div class="cell-wrapper" v-loading="loading">
    <svg-icon name="ic_close_new" class="icon_close" @click="closePopup" />
    <div v-if="!loading">
      <div class="cell top">
        <p class="title">
          <span>{{ detailData.storeName }}</span>
        </p>
        <div class="star-wrapper">
          <div class="type" v-if="detailData.storeTypeName">
            {{ detailData.storeTypeName }}
          </div>
          <p class="distance">
            {{
              `${detailData?.distance || ''}${detailData?.distanceUnit || ''}`
            }}
          </p>
        </div>
      </div>
      <div class="cell">
        <div class="block" v-if="detailData.address">
          <div class="img-block">
            <svg-icon name="icon-address-detail" class="left-img" />
          </div>
          <custom-text-ellipsis
            class="text-ellipsis"
            style="width: 90%"
            :rows="3"
            :content="detailData.address"
            :collapse-text="$t('selfRepair.btn_collapse')"
            :expand-text="$t('selfRepair.btn_expand')" />
        </div>
        <div class="block margin-20">
          <div class="img-block">
            <svg-icon name="icon-time-detail" class="left-img" />
          </div>
          <div class="right-block">
            <span v-if="getCurrentOpeningTime().opening == 1" class="time">
              {{ $t('serviceOutlets.close_tip') }}
            </span>
            <div v-else>
              <p v-for="(item, ix) in defaultDateList" :key="ix" class="time">
                {{ item }}
              </p>
            </div>
          </div>
          <div @click="showMoreTimeClick" class="more-time">
            {{ $t('serviceOutlets.more_time') }}
          </div>
        </div>
        <div class="block margin-20" v-if="detailData.email">
          <div class="img-block">
            <svg-icon name="icon-mail-detail" class="left-img" />
          </div>

          <p class="right-text">{{ detailData.email }}</p>
        </div>
        <div class="block margin-20" v-if="detailData.telephone">
          <div class="img-block">
            <svg-icon name="icon-phone-detail" class="left-img" />
          </div>
          <p class="right-text">{{ detailData.telephone }}</p>
        </div>
      </div>
      <!--  -->
      <div
        class="cell"
        v-if="detailData?.serviceTypeList && detailData.serviceTypeList.length">
        <p class="top-title">{{ $t('serviceOutlets.service_type') }}</p>
        <div class="type-wrapper">
          <div
            class="type-item"
            v-for="(item, ix) in detailData.serviceTypeList"
            :key="ix">
            <img :src="item.lightUrl" class="l_img" />
            <p class="text max-line2">
              {{ item.title }}
            </p>
          </div>
        </div>
      </div>
      <!--  -->
      <div
        class="cell"
        v-if="detailData.serviceCodeList && detailData.serviceCodeList.length">
        <p class="top-title">{{ $t('serviceOutlets.product_type') }}</p>
        <div class="type-wrapper">
          <div
            class="product-item"
            v-for="item in detailData.serviceCodeList"
            :key="item.code">
            <img :src="item.lightUrl" class="b_img" />
            <p class="text max-line2">{{ item.name }}</p>
          </div>
        </div>
      </div>
      <div class="cell" v-if="detailData.description">
        <p class="top-title">{{ $t('serviceOutlets.more_information') }}</p>
        <div class="ellipsis-text" v-html="detailData.description"></div>
      </div>
    </div>
    <store-opening-hours
      :info="detailData.openingHoursList"
      :allInfo="detailData"
      v-if="showTime"
      :visible="showTime"
      :overlayShow="true"
      class="opening-dialog"
      @close="showTime = false"
      :timeTextData="{
        businessHoursText,
        storeCloseTiText,
        timeTipText
      }" />
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { getDetailData } from '@support/api';
import { getAsyncData } from '@support/utils';
import { watch } from 'vue';
const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  longitude: {
    default: ''
  },
  latitude: {
    default: ''
  },
  timeTextData: {
    type: Object,
    default: {}
  },
  isLoading: {
    type: Boolean,
    default: false
  }
});
const timeTextData = ref(props.timeTextData);

let loading = ref(props.isLoading);
const { t } = useI18n();

const showTime = ref(false);

const detailData: any = ref({});
const defaultDateList = ref([]);
const { $sensorTrack } = useNuxtApp();

const businessHoursText = t('serviceOutlets.business_hours');
const storeCloseTiText = t('serviceOutlets.store_close_tip');
const timeTipText = t('serviceOutlets.time_tip');

watch(
  () => props.timeTextData,
  newValue => {
    timeTextData.value = newValue;
  }
);

watch(
  () => props.isLoading,
  newValue => {
    loading.value = newValue;
  }
);

const showMoreTimeClick = () => {
  // emits('getDetailInfo', detailData.value);
  $sensorTrack('overseas_service_store_details_moretime_click', {});
  showTime.value = true;
};

function getCurrentOpeningTime() {
  const { openingHoursList } = detailData.value;
  if (!openingHoursList) return {};
  let currentDay = new Date().getDay();
  if (currentDay == 0) {
    currentDay = 7;
  }
  const res = openingHoursList.find((it: any) => it.weekNum === currentDay);
  return res;
}

getDetail();
async function getDetail() {
  loading.value = true;
  try {
    const { data } = await useAsyncData(() =>
      getDetailData({
        id: props.id,
        latitude: props.latitude,
        longitude: props.longitude
      })
    );
    detailData.value = getAsyncData(data).data;
    let week = new Date().getDay();
    if (week === 0) week = 7;
    defaultDateList.value = detailData.value.openingHoursList.find(
      (it: any) => it.weekNum === week
    ).calendList;

    $sensorTrack('overseas_service_store_details_exposure', {
      store_id: props.id,
      store_name: detailData.value.storeName
    });
  } catch (error) {
    console.log('error');
  } finally {
    loading.value = false;
  }
}

const emits = defineEmits(['clickCancel', 'getDetailInfo']);
const closePopup = () => {
  emits('clickCancel');
};
</script>
<style lang="scss" scoped>
:deep(.text-ellipsis .text-box) {
  opacity: 0.6 !important;
  font-size: 14px !important;
  font-weight: 400;
  color: #1e253d !important;
}
p {
  margin: 0;
}
.time-popup {
  overflow: visible;
}
.icon_close {
  position: absolute;
  top: 8px;
  left: calc(50% + 211px);
  width: 24px;
  height: 24px;
  cursor: pointer;
  z-index: 1000;
}
.cell-wrapper {
  width: 100%;
  height: 100%;
  border-radius: 20px;
  position: relative;
  overflow-y: overlay;
  margin: 0;
}

.cell-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 10px;
  -webkit-border-radius: 6px;
  -moz-border-radius: 6px;
  border-radius: 6px;
}
.cell-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.cell-wrapper::-webkit-scrollbar-thumb {
  width: 4px;
  background-color: rgba(0, 0, 0, 0.2);
  background-clip: padding-box;
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  border-radius: 5px;
  min-height: 28px;
}

.cell-wrapper::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.2);
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  border-radius: 5px;
}

.opening-dialog {
  position: fixed;
  left: calc(50% - 180px);
  top: calc(50% - 158px - 54px);
  z-index: 1100;
  :deep(.icon-close-time) {
    position: absolute;
    left: calc(90% - 2px) !important;
    top: -41px !important;
    width: 24px;
    height: 24px;
    cursor: pointer;
  }
  :deep(.timer-wrapper) {
    left: 450px;
  }
}

// .cell-wrapper::-webkit-scrollbar {
//   width: 0px !important;
// }
.top.cell {
  padding-top: 0 !important;
}

.cell:last-child {
  border-bottom: unset;
}
.cell {
  position: relative;
  padding: 20px;
  overflow: visible;
  border-bottom: 1px solid rgba($color: #000000, $alpha: 0.15);
  .ellipsis-text {
    margin-top: 15px;
    font-size: 14px;
    color: #1e253d;
  }

  .top-title {
    font-size: 16px;
    color: #1e253d;
    font-weight: 500;
    line-height: 24px;
  }

  .title {
    font-weight: 500;
    width: 420px;
    font-size: 24px;
    line-height: 34px;
    text-align: left;
    overflow: hidden;
    display: -webkit-box;

    color: #000000;
    font-style: normal;
    text-transform: none;
    margin-top: 4px;
    margin-left: 4px;
  }

  .title span {
    display: block;
    width: 100%;
  }

  .star-wrapper {
    display: flex;
    align-items: center;
    margin-top: 10px;

    // margin-top: 5px;
    .fraction {
      font-size: 12px;
      color: #3475ff;
      margin-left: 5px;
    }
    .distance {
      margin-left: 20px;
      font-size: 12px;
      color: #1e253d;
      opacity: 0.6;
    }
  }
  .type {
    color: #1e253d;
    opacity: 0.8;
    background-color: #ebecf0;
    font-size: 12px;
    border-radius: 4px;
    // width: 96px;
    padding: 0 10px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .type-wrapper {
    padding-top: 8px;
    flex-wrap: wrap;
    .type-item,
    .product-item {
      display: inline-flex;
      align-items: center;
      margin-top: 10px;
      width: 50%;
      .b_img {
        width: 30px;
        height: 30px;
        margin-bottom: 10px;
      }
      .l_img {
        width: 20px;
        height: 20px;
        min-width: 20px;
        margin-right: 5px;
      }
      .text {
        font-size: 14px;
        color: #1e253d;
        line-height: 18px;
        text-align: center;
        opacity: 0.6;

        font-weight: 400;
        font-style: normal;
        text-transform: none;
      }
    }
  }
  .product-item {
    width: 25% !important;
    flex-direction: column;
    justify-content: space-between;
    p.text {
      width: 100%;
    }
  }

  .margin-20 {
    margin-top: 15px;
  }
  .block {
    display: flex;
    align-items: center;
    position: relative;
    .img-block {
      display: flex;
      align-items: center;
    }
    .more-time {
      position: absolute;
      color: #2e70ff;
      font-size: 14px;
      line-height: 18px;
      right: 0;
      cursor: pointer;
      width: 100px;
      font-weight: 400;
      line-height: 18px;
      font-style: normal;
      text-transform: none;
      text-align: right;
    }

    .left-img {
      width: 24px;
      height: 24px;
      margin-right: 15px;
    }
    .right-text {
      opacity: 0.6;
      word-break: break-all;
      font-weight: 400;
      font-size: 14px;
      color: #1e253d;
      line-height: 18px;
      text-align: left;
      font-style: normal;
      text-transform: none;
    }
    .right-block {
      display: flex;
      flex-direction: column;
      .time {
        color: #1e253d;
        opacity: 0.6;
        font-weight: 400;
        font-size: 14px;
        text-align: left;
        font-style: normal;
        line-height: 20px;
      }
    }
  }
}
</style>
